import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { v4 as uuid } from 'uuid';
import '../styles/BlogEditor.css';

const API_BASE = 'https://i0nzyjj5hc.execute-api.us-east-1.amazonaws.com/prod';

class QuillErrorBoundary extends React.Component {
  constructor(props) { 
    super(props); 
    this.state = { err: null }; 
  }
  componentDidCatch(err, info) { 
    console.error('[Quill render error]', err, info); 
    this.setState({ err }); 
  }
  render() { 
    return this.state.err ? 
      <div style={{color:'#b91c1c'}}>Editor failed to render — see console</div> : 
      this.props.children; 
  }
}

// Link Modal Component
function LinkModal({ isOpen, onClose, onSave, initialText = '', initialUrl = '' }) {
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    setText(initialText);
    setUrl(initialUrl);
  }, [initialText, initialUrl]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(text, url);
    onClose();
  };

  const handleRemove = () => {
    onSave('', '');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', padding: '24px', borderRadius: '8px',
        width: '400px', maxWidth: '90vw'
      }}>
        <h3 style={{ margin: '0 0 16px 0' }}>Edit Link</h3>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Text:</label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{
              width: '100%', padding: '8px', border: '1px solid #ccc',
              borderRadius: '4px', fontSize: '14px'
            }}
            placeholder="Link text"
          />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>URL:</label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{
              width: '100%', padding: '8px', border: '1px solid #ccc',
              borderRadius: '4px', fontSize: '14px'
            }}
            placeholder="https://example.com"
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {initialUrl && (
            <button
              onClick={handleRemove}
              style={{
                padding: '8px 16px', backgroundColor: '#dc2626', color: 'white',
                border: 'none', borderRadius: '4px', cursor: 'pointer'
              }}
            >
              Remove Link
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', backgroundColor: '#6b7280', color: 'white',
              border: 'none', borderRadius: '4px', cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!text || !url}
            style={{
              padding: '8px 16px', backgroundColor: text && url ? '#3b82f6' : '#9ca3af',
              color: 'white', border: 'none', borderRadius: '4px',
              cursor: text && url ? 'pointer' : 'not-allowed'
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BlogEditor({ onSave }) {
  console.log('🔥 BlogEditor component loaded');
  const { id } = useParams();
  const quillRef = useRef(null);
  const [isBlogAdmin, setIsBlogAdmin] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', content: '', status: 'DRAFT', heroImageKey: '', heroImageUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [linkModal, setLinkModal] = useState({ isOpen: false, text: '', url: '', range: null });
  const isEdit = Boolean(id);

  useEffect(() => {
    console.log('🚀 BlogEditor mounted');
    console.log('📆 ReactQuill available:', !!ReactQuill);
    console.log('📆 Quill available:', !!Quill);
    console.log('📆 Form state:', form);
  }, []);

  const getAuthToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString();
      return idToken;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.idToken?.toString();
        if (!token) {
          setIsBlogAdmin(false);
          return;
        }
        
        const payload = JSON.parse(atob(token.split('.')[1]));
        const groups = payload['cognito:groups'] || [];
        const isAdmin = groups.includes('blog-admin');
        
        console.log('🔑 Admin check result:', { isAdmin, groups });
        setIsBlogAdmin(isAdmin);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsBlogAdmin(false);
      }
    };
    
    checkAdminStatus();
  }, []);

  const fetchPost = useCallback(async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;
      
      const res = await fetch(`${API_BASE}/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok && data) {
        const formData = {
          ...data,
          excerpt: data.excerpt || '',
          content: typeof data.content === 'string' ? data.content : ''
        };
        
        setForm(formData);
        if (data.heroImageUrl) setPreviewUrl(data.heroImageUrl);
      }
    } catch (err) {
      console.error('[fetchPost] Error loading post:', err);
    }
  }, [id, getAuthToken]);

  useEffect(() => {
    if (isEdit && isBlogAdmin) fetchPost();
  }, [id, isEdit, isBlogAdmin, fetchPost]);

  console.log('🔍 BlogEditor render check:', { isBlogAdmin, isEdit, id });
  
  if (isBlogAdmin === null) {
    console.log('⏳ Waiting for admin status...');
    return null;
  }
  if (!isBlogAdmin) {
    console.log('❌ Access denied - not blog admin');
    return <p className="denied">Access denied (blog-admin only).</p>;
  }
  
  console.log('✅ Rendering BlogEditor with Quill');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (html) => {
    setForm(prev => ({ ...prev, content: html || '' }));
  };

  const handleImage = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const token = await getAuthToken();
      if (!token) {
        alert('You must be logged in to upload an image.');
        return;
      }
      const res = await fetch(`${API_BASE}/upload-url?filename=${encodeURIComponent(file.name)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { uploadUrl, key } = await res.json();
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });
      const imageUrl = `https://skroop-blog.s3.amazonaws.com/${key}`;
      setForm(prev => ({ ...prev, heroImageKey: key, heroImageUrl: imageUrl }));
      setPreviewUrl(imageUrl);
    } catch (err) {
      alert('Image upload failed: ' + err.message);
    }
  };

  const handleSubmit = async publish => {
    if (!form.slug) return alert('Slug is required.');
    setLoading(true);
    setSuccessMessage('');
    try {
      const token = await getAuthToken();
      if (!token) {
        alert('You must be logged in to submit a post.');
        return;
      }
      const payload = { ...form, status: publish ? 'PUBLISHED' : 'DRAFT' };
      if (!isEdit) payload.id = uuid();
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit ? `${API_BASE}/posts/${id}` : `${API_BASE}/posts`;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Failed to save');
      if (!isEdit) {
        setForm({ title: '', slug: '', excerpt: '', content: '', status: 'DRAFT', heroImageKey: '', heroImageUrl: '' });
        setPreviewUrl(null);
      }
      setSuccessMessage(publish ? 'Post published successfully!' : 'Draft saved successfully!');
      onSave?.();
    } catch (err) {
      alert('Unexpected error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog-editor">
      <section className="editor-content">
        <h1>{isEdit ? 'Edit Post' : 'New Post'}</h1>
        {successMessage && <div className="success-message">{successMessage}</div>}
        <input name="title" placeholder="Title" value={form.title} onChange={handleChange} />
        <input name="slug" placeholder="Slug (required)" value={form.slug} onChange={handleChange} required />
        <textarea name="excerpt" placeholder="Excerpt" value={form.excerpt || ''} onChange={handleChange} rows={3} />
        <div className="quill-container">
          <QuillErrorBoundary>
            <ReactQuill 
              ref={quillRef}
              value={form.content} 
              onChange={handleContentChange} 
              placeholder="Write your blog here..."

              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                  [{ 'font': [] }],
                  [{ 'size': ['small', false, 'large', 'huge'] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'script': 'sub'}, { 'script': 'super' }],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'indent': '-1'}, { 'indent': '+1' }],
                  [{ 'align': [] }],
                  ['blockquote', 'code-block'],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              formats={[
                'header', 'font', 'size',
                'bold', 'italic', 'underline', 'strike',
                'script', 'color', 'background',
                'list', 'bullet', 'indent', 'align',
                'blockquote', 'code-block', 'link', 'image'
              ]}
            />
          </QuillErrorBoundary>
        </div>
        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <input type="file" accept="image/*" onChange={handleImage} />
        </div>
        {previewUrl && <img src={previewUrl} alt="Preview" className="hero-preview" />}
        <div className="actions" style={{ marginTop: '1.5rem' }}>
          <button onClick={() => handleSubmit(false)} disabled={loading}>Save Draft</button>
          <button onClick={() => handleSubmit(true)} disabled={loading}>Publish</button>
        </div>
      </section>
      <LinkModal
        isOpen={linkModal.isOpen}
        onClose={() => setLinkModal({ isOpen: false, text: '', url: '', range: null })}
        onSave={() => {}}
        initialText={linkModal.text}
        initialUrl={linkModal.url}
      />
    </div>
  );
}